import React, { useEffect, useMemo, useState } from 'react';

import { useMutation, useQueryClient, useQuery as useTanstackQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import { currencyCodes } from '~/globals';

import { Box, Container, MenuItem, Stack, TextField } from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';
import PageSubheader from '~/components/Common/Elements/PageSubheader';
import Spinner from '~/components/Common/Spinner';
import VoucherConditions from '~/components/Vouchers/VoucherConditions';
import VoucherLogsTable from '~/components/Vouchers/VoucherLogsTable';
import VoucherRewards from '~/components/Vouchers/VoucherRewards';
import VoucherUsers from '~/components/Vouchers/VoucherUsers';

import VoucherFormNavigation from '~/pages/Vouchers/VoucherFormNavigation';

import { useCreateVoucherUsers } from '~/hooks/apis/promo/useCreateVoucherUsers';

import {
  CreateVoucherPayload,
  CreateVoucherResponse,
  UpdateVoucherPayload,
  UpdateVoucherResponse,
  Voucher,
  createVoucher,
  getVoucher,
  updateVoucher,
} from '~/services/PromoService';

type RouteParams = {
  voucherId?: string;
};

type VoucherReward = NonNullable<CreateVoucherPayload['voucher_rewards']>[number];
type VoucherCondition = NonNullable<CreateVoucherPayload['voucher_conditions']>[number];
type VoucherApiFields = {
  voucherName?: string;
  currencyCode?: string;
  voucherRewards?: VoucherReward[];
  voucherConditions?: VoucherCondition[];
  voucher_name?: string;
  currency_code?: string;
  voucher_rewards?: VoucherReward[];
  voucher_conditions?: VoucherCondition[];
};

const DEFAULT_REWARD_AMOUNT = 50;
const DEFAULT_REWARD_PRODUCTS: Array<VoucherReward['products'][number]> = ['hotel-ALL', 'tour-ALL', 'cruise-ALL'];

type VoucherFormSnapshotInput = {
  voucherName: string;
  expiredAt: string;
  currencyCode: string;
  voucherRewards: VoucherReward[];
  voucherConditions: VoucherCondition[];
  voucherUsers: string[];
};

const buildFormSnapshot = (data: VoucherFormSnapshotInput) => {
  return JSON.stringify({
    voucherName: data.voucherName.trim(),
    expiredAt: data.expiredAt.trim(),
    currencyCode: data.currencyCode,
    voucherRewards: data.voucherRewards.map((reward) => ({
      ...reward,
      reward_amount: Number(reward.reward_amount),
      min_spend: reward.min_spend == null ? undefined : Number(reward.min_spend),
    })),
    voucherConditions: data.voucherConditions,
    voucherUsers: data.voucherUsers,
  });
};

const normalizeExpiredAt = (value: string) => {
  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return '';
  }
  return trimmedValue.includes('T') ? trimmedValue : `${trimmedValue}T00:00:00.000Z`;
};

const buildDefaultRewards = (): VoucherReward[] => {
  return [
    {
      id_voucher_reward: uuid(),
      reward_amount: DEFAULT_REWARD_AMOUNT,
      products: DEFAULT_REWARD_PRODUCTS,
      min_spend: undefined,
      min_spend_products: ['ALL-ALL'],
    },
  ];
};

export default function VoucherFormPage() {
  const { voucherId } = useParams<RouteParams>();
  const [showAdvanceDescription, setShowAdvanceDescription] = useState(false);
  const isEdit = Boolean(voucherId);
  const history = useHistory();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [voucherName, setVoucherName] = useState('');
  const [expiredAt, setExpiredAt] = useState('');
  const [currencyCode, setCurrencyCode] = useState('AUD');
  const [voucherRewards, setVoucherRewards] = useState<VoucherReward[]>([]);
  const [voucherConditions, setVoucherConditions] = useState<VoucherCondition[]>([]);
  const [voucherUsers, setVoucherUsers] = useState<string[]>([]);
  const [errors, setErrors] = useState<{ voucherName?: string; currencyCode?: string }>({});
  const [initialSnapshot, setInitialSnapshot] = useState<string | null>(null);

  const { data: voucherResponse, isLoading: isVoucherLoading } = useTanstackQuery({
    queryKey: ['voucher', voucherId],
    queryFn: async () => {
      if (!voucherId) {
        return null;
      }
      return await getVoucher(voucherId);
    },
    enabled: Boolean(voucherId),
  });

  const voucher: Voucher | null = useMemo(() => {
    return voucherResponse?.result ?? null;
  }, [voucherResponse]);

  useEffect(() => {
    if (!isEdit) {
      const defaultRewards = buildDefaultRewards();
      setVoucherName('');
      setExpiredAt('');
      setCurrencyCode('AUD');
      setVoucherRewards(defaultRewards);
      setVoucherConditions([]);
      setVoucherUsers([]);
      setErrors({});
      setInitialSnapshot(
        buildFormSnapshot({
          voucherName: '',
          expiredAt: '',
          currencyCode: 'AUD',
          voucherRewards: defaultRewards,
          voucherConditions: [],
          voucherUsers: [],
        }),
      );
      return;
    }
    if (voucher) {
      const voucherData = voucher as VoucherApiFields;
      const nextVoucherName = voucherData.voucherName || voucherData.voucher_name || '';
      if (
        (voucherData.currencyCode || voucherData.currency_code) &&
        currencyCodes.includes((voucherData.currencyCode || voucherData.currency_code) as string)
      ) {
        setCurrencyCode((voucherData.currencyCode || voucherData.currency_code) as string);
      } else {
        setCurrencyCode('AUD');
      }
      const nextVoucherRewards = (voucherData.voucherRewards ?? voucherData.voucher_rewards ?? []).map((reward) => ({
        ...reward,
        id_voucher_reward: reward.id_voucher_reward || uuid(),
      }));
      const nextVoucherConditions = (voucherData.voucherConditions ?? voucherData.voucher_conditions ?? []).map(
        (condition) => ({
          ...condition,
          id_voucher_condition: condition.id_voucher_condition || uuid(),
        }),
      );
      setVoucherName(nextVoucherName);
      setVoucherRewards(nextVoucherRewards);
      setVoucherConditions(nextVoucherConditions);
      setVoucherUsers([]);
      setInitialSnapshot(
        buildFormSnapshot({
          voucherName: nextVoucherName,
          expiredAt: '',
          currencyCode:
            (voucherData.currencyCode || voucherData.currency_code) &&
            currencyCodes.includes((voucherData.currencyCode || voucherData.currency_code) as string)
              ? ((voucherData.currencyCode || voucherData.currency_code) as string)
              : 'AUD',
          voucherRewards: nextVoucherRewards,
          voucherConditions: nextVoucherConditions,
          voucherUsers: [],
        }),
      );
    }
  }, [isEdit, voucher]);

  const currentSnapshot = useMemo(() => {
    return buildFormSnapshot({
      voucherName,
      expiredAt,
      currencyCode,
      voucherRewards,
      voucherConditions,
      voucherUsers,
    });
  }, [voucherName, expiredAt, currencyCode, voucherRewards, voucherConditions, voucherUsers]);

  const hasUnsavedChanges = initialSnapshot !== null && initialSnapshot !== currentSnapshot;

  useEffect(() => {
    if (!hasUnsavedChanges) {
      return undefined;
    }
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (!hasUnsavedChanges) {
      return undefined;
    }
    const unblock = history.block(() => {
      const shouldLeave = window.confirm('You have unsaved changes. Are you sure you want to leave this page?');
      if (!shouldLeave) {
        return false;
      }
      return undefined;
    });
    return () => unblock();
  }, [hasUnsavedChanges, history]);

  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash === 'users') {
      const usersElement = document.getElementById('users');
      if (usersElement) {
        usersElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [location.hash]);

  const createMutation = useMutation<CreateVoucherResponse, Error, CreateVoucherPayload>({
    mutationFn: async (payload) => createVoucher(payload),
  });

  const updateMutation = useMutation<UpdateVoucherResponse, Error, { id: string; payload: UpdateVoucherPayload }>({
    mutationFn: async ({ id, payload }) => updateVoucher(id, payload),
  });

  const createVoucherUsersMutation = useCreateVoucherUsers();

  const validateForm = () => {
    const newErrors: { voucherName?: string; currencyCode?: string } = {};
    if (!voucherName.trim()) {
      newErrors.voucherName = 'Voucher name is required';
    }
    if (!currencyCode) {
      newErrors.currencyCode = 'Currency code is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const normalizedRewards = voucherRewards.map((reward) => ({
        ...reward,
        reward_amount: Number(reward.reward_amount),
        min_spend: reward.min_spend == null ? undefined : Number(reward.min_spend),
      }));
      let savedVoucherId = voucherId || '';
      if (isEdit) {
        const payload: UpdateVoucherPayload = {
          ...(voucherName.trim() ? { voucher_name: voucherName.trim() } : {}),
          ...(currencyCode ? { currency_code: currencyCode } : {}),
          voucher_rewards: normalizedRewards as UpdateVoucherPayload['voucher_rewards'],
          voucher_conditions: voucherConditions,
        };
        await updateMutation.mutateAsync({ id: voucherId || '', payload });
      } else {
        const payload: CreateVoucherPayload = {
          voucher_name: voucherName.trim(),
          currency_code: currencyCode,
          voucher_rewards: normalizedRewards,
          voucher_conditions: voucherConditions,
        };
        const createResponse = await createMutation.mutateAsync(payload);
        savedVoucherId = createResponse?.result?.voucherId || '';
      }

      if (voucherUsers.length > 0 && savedVoucherId) {
        await createVoucherUsersMutation.mutateAsync({
          voucher_id: savedVoucherId,
          ...(expiredAt.trim() ? { expired_at: normalizeExpiredAt(expiredAt) } : {}),
          voucher_users: voucherUsers.map((userId) => ({ user_id: userId })),
        });
      }
      setInitialSnapshot(currentSnapshot);
      enqueueSnackbar(isEdit ? 'Updated voucher successfully' : 'Created voucher successfully', {
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['vouchers'] });
      if (!isEdit && savedVoucherId) {
        history.replace(`/marketing/vouchers/${savedVoucherId}`);
      }
    } catch (error) {
      enqueueSnackbar(`Failed to save voucher: ${(error as Error).message}`, { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isEdit && isVoucherLoading) {
    return <Spinner />;
  }

  return (
    <Container maxWidth="xl">
      <PageHeader title={isEdit ? 'Edit Voucher' : 'Create Voucher'}>
        <VoucherFormNavigation
          isSubmitting={isSubmitting}
          isEdit={isEdit}
          showAdvanceDescription={showAdvanceDescription}
          onBack={() => history.push('/marketing/vouchers')}
          onSubmit={handleSubmit}
          onToggleAdvancedDescription={() => setShowAdvanceDescription(!showAdvanceDescription)}
        />
      </PageHeader>

      <Stack spacing={10}>
        <Stack spacing={2}>
          <TextField
            label="Voucher Name"
            value={voucherName}
            onChange={(e) => {
              setVoucherName(e.target.value);
              if (errors.voucherName && e.target.value.trim()) {
                setErrors({ ...errors, voucherName: undefined });
              }
            }}
            error={!!errors.voucherName}
            helperText={
              errors.voucherName || showAdvanceDescription
                ? 'Name displayed for the voucher. This will be displayed to the user during checkout.'
                : undefined
            }
            fullWidth
            required
            disabled={isSubmitting}
          />
          <TextField
            label="Currency Code"
            value={currencyCode}
            onChange={(event) => {
              setCurrencyCode(event.target.value as string);
              if (errors.currencyCode) {
                setErrors({ ...errors, currencyCode: undefined });
              }
            }}
            error={!!errors.currencyCode}
            helperText={errors.currencyCode || showAdvanceDescription ? 'Select an ISO currency code' : undefined}
            fullWidth
            required
            disabled={isSubmitting}
            select
          >
            {currencyCodes.map((code) => (
              <MenuItem key={code} value={code}>
                {code}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        <Box mt={3}>
          <PageSubheader title="Rewards">
            {showAdvanceDescription && (
              <Box component="span" color="text.secondary" alignItems="left">
                The amount the voucher is worth, and for which products. Only one reward is applied to an order.
              </Box>
            )}
          </PageSubheader>
          <VoucherRewards rewards={voucherRewards} onChange={setVoucherRewards} disabled={isSubmitting} />
        </Box>
        <Box mt={3}>
          <PageSubheader title="Conditions" />
          <VoucherConditions
            conditions={voucherConditions}
            rewards={voucherRewards}
            onChange={setVoucherConditions}
            showAdvanceDescription={showAdvanceDescription}
            disabled={isSubmitting}
          />
        </Box>
        <VoucherUsers
          userIds={voucherUsers}
          onChange={setVoucherUsers}
          expiredAt={expiredAt}
          onExpiredAtChange={setExpiredAt}
          showAdvanceDescription={showAdvanceDescription}
          disabled={isSubmitting}
          voucherId={voucherId}
        />
        {isEdit && (
          <VoucherLogsTable
            voucherId={voucherId}
            queryOptions={{
              page: 0,
              limit: 20,
              sortBy: 'created_at',
              sortByDirection: 'desc',
            }}
          />
        )}
      </Stack>
    </Container>
  );
}
